import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import {
  Apple,
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  Calendar,
  ChartNoAxesColumnIncreasing,
  Check,
  ChevronDown,
  ChevronsLeft,
  CirclePlay,
  CircleQuestionMark,
  ClipboardClock,
  Download,
  Ellipsis,
  FileQuestionMark,
  FolderInput,
  FolderPlus,
  Folders,
  GalleryThumbnails,
  Gauge,
  Globe,
  GraduationCap,
  Hash,
  House,
  ListPlus,
  ListX,
  Info,
  Link2,
  LUCIDE_ICONS,
  LucideAngularModule,
  LucideIconProvider,
  LucideIcons,
  MessageSquare,
  Pencil,
  Pin,
  PinOff,
  Plus,
  MessagesSquare,
  School,
  Settings,
  SquarePlay,
  TriangleAlert,
  UserCog,
  Users,
} from 'lucide-angular';
import { SVGIconComponentsModule } from 'src/app/common/svg-icon/svg-icon-components.module';

export enum IconTypes {
  matIcon = 'mat-icon',
  svgIcon = 'svg-icon',
  img = 'img',
  materialIcon = 'material-icon',
  lucideIcon = 'lucide-icon',
}

export interface IconConfig {
  name: string;
  type: IconTypes;
  size?: number;
  outlined?: boolean;
  wrapperStyle?: { [key: string]: string | number };
  onClick?: () => void;
}

const PENCIL_LUCIDE_ICONS: LucideIcons = {
  Apple,
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  Calendar,
  ChartNoAxesColumnIncreasing,
  Check,
  ChevronDown,
  ChevronsLeft,
  CirclePlay,
  CircleQuestionMark,
  ClipboardClock,
  Download,
  Ellipsis,
  FileQuestionMark,
  FolderInput,
  FolderPlus,
  Folders,
  GalleryThumbnails,
  Gauge,
  Globe,
  GraduationCap,
  Hash,
  House,
  ListPlus,
  ListX,
  Info,
  Link2,
  MessageSquare,
  Pencil,
  Pin,
  PinOff,
  Plus,
  MessagesSquare,
  School,
  Settings,
  SquarePlay,
  TriangleAlert,
  UserCog,
  Users,
};

/**
 * Single multi-provider for the Lucide icons we ship in the app.
 * Registered once at AppModule level so every `<app-pencil-icon>` and `<lucide-icon>`
 * shares the same `LucideIconProvider` instance instead of allocating one per component.
 */
export const PENCIL_LUCIDE_ICONS_PROVIDER = {
  provide: LUCIDE_ICONS,
  multi: true,
  useValue: new LucideIconProvider(PENCIL_LUCIDE_ICONS),
};

@Component({
  selector: 'app-pencil-icon[name][type]',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, MatIconModule, SVGIconComponentsModule],
  templateUrl: './pencil-icon.component.html',
  styleUrls: ['./pencil-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PencilIconComponent {
  @HostBinding('style') hostStyle = {};
  @HostListener('click') onClick() {
    if (this.clickAction) {
      this.clickAction();
    }
  }

  @Input() name?: string;
  @Input() type: string = IconTypes.svgIcon;
  @Input() size?: number;
  @Input() outlined = false;
  @Input() clickAction?: () => void;
  @Input() set wrapperStyle(value: { [key: string]: string | number } | undefined) {
    this.hostStyle = value ?? {};
  }
}
